import { SOUTH_AMERICA_COUNTRIES } from '@etiquetachef/types';
import { CONTINENT_KEYS, type ContinentKey } from './worldCountries';

/**
 * Todos os locais (ISO-3166 alpha-2) da tabela de distribuição da Google Play,
 * agrupados por continente — o universo que o super-admin marca país a país em
 * `/billing/addons` quando desliga "Todos os países" (`["*"]`).
 *
 * Origem: #954 — 2026-10-13 08:47 +0200.
 *
 * Por quê: o add-on precisa poder ser habilitado em qualquer lugar onde o app
 * é distribuído, não só nos 5 mercados de `Country`. **O sistema não exclui
 * país nenhum**: quem exclui é o console da Google Play (owner, 2026-10-13).
 * Por isso a lista traz os 225 locais da tabela, inclusive os 5 onde a Play
 * hoje não processa compra (CN, CU, IR, RU, SD) e os 48 do grupo "Rest of the
 * world" (só app grátis). A primeira versão desta lista filtrava pela coluna
 * de compra (172) — desfeito pelo owner no mesmo dia.
 *
 * Dados: capturado em 2026-10-13 de https://play.google.com/supported-locations
 * (versão "2026/01", publicada pela Google em 2026-09-28): as três tabelas
 * (Google LLC, Google Commerce Limited, Google Asia Pacific) e a lista "Rest of
 * the world". A página é renderizada por JS — `curl` só traz a casca; a
 * captura foi feita com Playwright. XK (Kosovo) não é ISO oficial, mas é o
 * código que a Play usa.
 *
 * Contrato: códigos únicos entre continentes (teste em
 * `playStoreCountries.test.ts`), sempre `^[A-Z]{2}$`. O schema do servidor
 * (`AddonCountriesSchema`, `packages/types/src/schemas.ts`) aceita qualquer
 * alpha-2 e o curinga `"*"` — esta lista só monta a tela.
 *
 * Usado por: `features/billing/AddonCountriesPicker.tsx` (seleção país a país)
 * e `features/billing/addonRow.ts` (`countriesByContinent`), admin web.
 *
 * Dicas: quando a Google mudar a tabela, recapture e atualize aqui; país que
 * sair da lista continua gravado nos add-ons e aparece no grupo "Outros" do
 * picker — nunca some em silêncio. Desde a #1078 a América do Norte tem 3
 * grupos — América do Norte (US, CA, MX, BM), América Central (BZ, CR, GT, HN,
 * NI, PA, SV) e Caribe (inclusive territórios) —, e a América do Sul é
 * `SOUTH_AMERICA_COUNTRIES` de `@etiquetachef/types`: a mesma lista que o
 * servidor usa para a região padrão (`defaultRegionForCountry`), para a coluna
 * "Região" nunca divergir do que a criação de tenant resolve. Os territórios
 * antárticos/subantárticos sem continente (AQ, BV, GS, HM, TF) ficam em Oceania.
 *
 * Issues: #954 (países da Play por continente, curinga "*"), #1078 (grupos da
 * região, F2 da #960).
 *
 * Histórico:
 *  - 2026-10-13 08:47 +0200 — (#954) 172 países que aceitam compra.
 *  - 2026-10-13 09:27 +0200 — (#954) 225 locais: o sistema não exclui país.
 *  - 2026-10-29 10:20 +0100 — (#1078, F2 da #960) América do Norte em 3 grupos;
 *    América do Sul derivada de `SOUTH_AMERICA_COUNTRIES`.
 */
export const PLAY_LOCATIONS_BY_CONTINENT: Readonly<Record<ContinentKey, readonly string[]>> = {
  africa: [
    'AO', 'BF', 'BI', 'BJ', 'BW', 'CD', 'CF', 'CG', 'CI', 'CM', 'CV', 'DJ', 'DZ',
    'EG', 'EH', 'ER', 'ET', 'GA', 'GH', 'GM', 'GN', 'GQ', 'GW', 'IO', 'KE', 'KM',
    'LR', 'LS', 'LY', 'MA', 'MG', 'ML', 'MR', 'MU', 'MW', 'MZ', 'NA', 'NE', 'NG',
    'RW', 'SC', 'SD', 'SH', 'SL', 'SN', 'SO', 'SS', 'ST', 'SZ', 'TD', 'TG', 'TN',
    'TZ', 'UG', 'ZA', 'ZM', 'ZW',
  ],
  northAmerica: ['BM', 'CA', 'MX', 'US'],
  centralAmerica: ['BZ', 'CR', 'GT', 'HN', 'NI', 'PA', 'SV'],
  caribbean: [
    'AG', 'AI', 'AW', 'BB', 'BQ', 'BS', 'CU', 'CW', 'DM', 'DO', 'GD', 'HT', 'JM',
    'KN', 'KY', 'LC', 'MS', 'TC', 'TT', 'VC', 'VG',
  ],
  southAmerica: SOUTH_AMERICA_COUNTRIES,
  asia: [
    'AE', 'AF', 'AM', 'AZ', 'BD', 'BH', 'BN', 'BT', 'CN', 'GE', 'HK', 'ID', 'IL',
    'IN', 'IQ', 'IR', 'JO', 'JP', 'KG', 'KH', 'KR', 'KW', 'KZ', 'LA', 'LB', 'LK',
    'MM', 'MN', 'MO', 'MV', 'MY', 'NP', 'OM', 'PH', 'PK', 'PS', 'QA', 'SA', 'SG',
    'TH', 'TJ', 'TL', 'TM', 'TR', 'TW', 'UZ', 'VN', 'YE',
  ],
  europe: [
    'AD', 'AL', 'AT', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE',
    'ES', 'FI', 'FR', 'GB', 'GG', 'GI', 'GR', 'HR', 'HU', 'IE', 'IM', 'IS', 'IT',
    'JE', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL',
    'PT', 'RO', 'RS', 'RU', 'SE', 'SI', 'SK', 'SM', 'UA', 'VA', 'XK',
  ],
  oceania: [
    'AQ', 'AU', 'BV', 'CC', 'CK', 'CX', 'FJ', 'FM', 'GS', 'HM', 'KI', 'NF', 'NR',
    'NU', 'NZ', 'PG', 'PN', 'SB', 'TF', 'TK', 'TO', 'TV', 'UM', 'VU', 'WS',
  ],
};

/** Todos os locais da Play, na ordem dos continentes. É o ponto de partida ao desligar "Todos os países". */
export const ALL_PLAY_LOCATIONS: readonly string[] = CONTINENT_KEYS.flatMap(
  (k) => PLAY_LOCATIONS_BY_CONTINENT[k],
);
